/**
 * Route guard for /backoffice/*. Requires a dashboard role from GET /api/auth/me.
 * Security Checklist: client-side route authorization; must be paired with server-side checks.
 *
 * RBAC: after auth is resolved we also check route-level permissions so users without
 * e.g. CAN_VIEW_LOGS cannot reach /backoffice/system-logs. Deep links redirect to a
 * dedicated access-denied screen.
 */

import { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@contexts/AuthContext';
import { BACKOFFICE_BASE, BACKOFFICE_LOGIN_PATH } from '@lib/backofficeRoutes';
import { getRequiredPermissionForPath } from '@lib/rbac';
import { usePermissions } from '@hooks/usePermissions';
import { AdminAccessDeniedPage } from '@pages/admin/AdminAccessDeniedPage';

// 'viewer' is a legacy / test role treated as read-only support-agent access.
const DASHBOARD_ROLES = new Set(['system_administrator', 'operations_manager', 'financial_officer', 'support_agent', 'viewer']);

export function AdminRouteGuard({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isAuthenticated, authLoading, userRole } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const role = String(userRole ?? '').trim().toLowerCase();

  const isPermissionDenied =
    !authLoading &&
    isAuthenticated &&
    DASHBOARD_ROLES.has(role) &&
    location.pathname !== `${BACKOFFICE_BASE}/access-denied` &&
    (() => {
      const required = getRequiredPermissionForPath(location.pathname, location.search);
      return !!required && !can(required);
    })();

  useEffect(() => {
    if (isPermissionDenied) {
      navigate(`${BACKOFFICE_BASE}/access-denied`, { replace: true });
    }
  }, [isPermissionDenied, navigate]);

  if (authLoading) {
    /** Visual preloader: {@link AuthLoadingOverlay} (path-aware; main column on /backoffice). */
    return (
      <span className="sr-only">
        {t('admin.auth.resolvingRole', { defaultValue: 'Loading session…' })}
      </span>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={BACKOFFICE_LOGIN_PATH} replace />;
  }

  if (!DASHBOARD_ROLES.has(role)) {
    return <Navigate to="/" replace />;
  }

  if (isPermissionDenied) {
    return <AdminAccessDeniedPage />;
  }

  return <>{children}</>;
}
